import React, { useState } from 'react';
import { ArchitectureSystem, SystemType } from '../types/system';
import { ApplicabilityDecision } from '../types/decision';
import { ControlObligation } from '../types/obligation';
import {
  X,
  Cpu,
  Plus,
  Edit2,
  Trash2,
  CheckCircle2,
  Layers,
  Database,
  Server,
  Cloud,
  Lock,
  ArrowRight,
  Shield,
  Filter
} from 'lucide-react';

interface SystemsInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  systems: ArchitectureSystem[];
  decisions: Record<string, ApplicabilityDecision>;
  controls: ControlObligation[];
  onSaveSystem: (system: ArchitectureSystem) => void;
  onDeleteSystem: (systemId: string) => void;
  onFilterBySystem: (systemId: string) => void;
}

const SYSTEM_TYPES: SystemType[] = [
  'Web / Mobile Application',
  'Microservice / Backend API',
  'Relational Database / Storage',
  'Data Warehouse / Analytics Lake',
  'Identity & Access Provider (IdP)',
  'Third-Party SaaS / Processor',
  'API Gateway / Cloud Edge',
  'Message Queue / Event Bus'
];

export default function SystemsInventoryModal({
  isOpen,
  onClose,
  systems,
  decisions,
  controls,
  onSaveSystem,
  onDeleteSystem,
  onFilterBySystem
}: SystemsInventoryModalProps) {
  if (!isOpen) return null;

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [idInput, setIdInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [typeInput, setTypeInput] = useState<SystemType>('Web / Mobile Application');
  const [ownerInput, setOwnerInput] = useState('');
  const [descInput, setDescInput] = useState('');
  const [dataCatInput, setDataCatInput] = useState('');

  const handleStartCreate = () => {
    const nextNum = systems.length + 1;
    setIdInput(`SYS-${String(nextNum).padStart(3, '0')}`);
    setNameInput('');
    setTypeInput('Web / Mobile Application');
    setOwnerInput('Platform Engineering');
    setDescInput('');
    setDataCatInput('Digital Personal Data');
    setEditingId(null);
    setIsEditing(true);
  };

  const handleStartEdit = (sys: ArchitectureSystem) => {
    setIdInput(sys.id);
    setNameInput(sys.name);
    setTypeInput(sys.type);
    setOwnerInput(sys.owner || '');
    setDescInput(sys.description);
    setDataCatInput((sys.data_categories || []).join(', '));
    setEditingId(sys.id);
    setIsEditing(true);
  };

  const handleSaveForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!idInput || !nameInput) return;

    const newSys: ArchitectureSystem = {
      id: idInput.trim().toUpperCase(),
      name: nameInput.trim(),
      type: typeInput,
      owner: ownerInput.trim() || 'Unassigned',
      description: descInput.trim() || 'Enterprise software component',
      data_categories: dataCatInput.split(',').map(s => s.trim()).filter(Boolean),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    onSaveSystem(newSys);
    setIsEditing(false);
  };

  // Count mapped obligations per system
  const getMappedCount = (sysId: string) => {
    return controls.filter(c => {
      const d = decisions[c.id];
      if (d?.systems_in_scope && d.systems_in_scope.length > 0) {
        return d.systems_in_scope.includes(sysId);
      }
      return (c.systems || '').includes(sysId);
    }).length;
  };

  const getTypeIcon = (type: SystemType) => {
    switch (type) {
      case 'Relational Database / Storage':
      case 'Data Warehouse / Analytics Lake':
        return <Database className="w-4 h-4 text-emerald-500" />;
      case 'Identity & Access Provider (IdP)':
        return <Lock className="w-4 h-4 text-purple-500" />;
      case 'Third-Party SaaS / Processor':
        return <Cloud className="w-4 h-4 text-sky-500" />;
      default:
        return <Server className="w-4 h-4 text-indigo-500" />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 sm:p-6 bg-slate-950/70 backdrop-blur-xs">
      <div className="relative w-full max-w-4xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[88vh]">
        
        {/* Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/80 dark:bg-slate-900/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <Cpu className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-black text-slate-900 dark:text-slate-100 tracking-tight">
                  Enterprise Architecture Systems & Asset Inventory
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
                  {systems.length} Registered Systems
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Define your organization's custom applications, databases, and microservices to map technical DPDP obligations
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isEditing && (
              <button
                onClick={handleStartCreate}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors shadow-xs"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Register System</span>
              </button>
            )}

            <button
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          
          {isEditing ? (
            /* CREATE / EDIT FORM */
            <form onSubmit={handleSaveForm} className="space-y-4 p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 pb-3">
                <div className="font-bold text-sm text-slate-900 dark:text-slate-100">
                  {editingId ? `Edit System: ${editingId}` : 'Register New Enterprise System'}
                </div>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="text-xs text-slate-400 hover:text-slate-600"
                >
                  Cancel
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    System Identifier / Code
                  </label>
                  <input
                    type="text"
                    required
                    value={idInput}
                    onChange={(e) => setIdInput(e.target.value)}
                    placeholder="e.g. APP-CORE-01"
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-mono font-bold text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                  />
                </div>

                <div className="sm:col-span-2 space-y-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    System Name
                  </label>
                  <input
                    type="text"
                    required
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder="e.g. Customer Onboarding & Lending App"
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    System Architecture Type
                  </label>
                  <select
                    value={typeInput}
                    onChange={(e) => setTypeInput(e.target.value as SystemType)}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                  >
                    {SYSTEM_TYPES.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    Technical Lead / Owner
                  </label>
                  <input
                    type="text"
                    value={ownerInput}
                    onChange={(e) => setOwnerInput(e.target.value)}
                    placeholder="e.g. Core Banking Engineering"
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  System Description & Role in Data Processing
                </label>
                <textarea
                  rows={2}
                  value={descInput}
                  onChange={(e) => setDescInput(e.target.value)}
                  placeholder="Describe purpose: captures user signup KYC, stores Aadhaar hash, coordinates webhook events..."
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  Personal Data Categories Handled (Comma separated)
                </label>
                <input
                  type="text"
                  value={dataCatInput}
                  onChange={(e) => setDataCatInput(e.target.value)}
                  placeholder="e.g. KYC Identity, Phone Number, Biometrics, Payment Transaction Logs"
                  className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-400 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors"
                >
                  {editingId ? 'Save Changes' : 'Register System'}
                </button>
              </div>
            </form>
          ) : (
            /* SYSTEM CARDS LIST */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {systems.map((sys) => {
                const mappedCount = getMappedCount(sys.id);
                return (
                  <div
                    key={sys.id}
                    className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-3 hover:border-slate-400 dark:hover:border-slate-600 transition-all flex flex-col justify-between"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-lg text-xs font-mono font-extrabold bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30 whitespace-nowrap shrink-0">
                            {sys.id}
                          </span>
                          <span className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                            {getTypeIcon(sys.type)}
                            <span>{sys.type}</span>
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleStartEdit(sys)}
                            className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
                            title="Edit System"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm(`Delete system ${sys.id} (${sys.name})?`)) {
                                onDeleteSystem(sys.id);
                              }
                            }}
                            className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                            title="Delete System"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100">
                        {sys.name}
                      </h4>

                      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-2">
                        {sys.description}
                      </p>

                      {sys.owner && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                          Owner: <strong className="text-slate-700 dark:text-slate-300">{sys.owner}</strong>
                        </div>
                      )}
                    </div>

                    {/* Bottom Action / Obligation Mapping Count */}
                    <div className="pt-2 border-t border-slate-200 dark:border-slate-700/80 flex items-center justify-between text-xs">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-accent/10 text-accent dark:text-accent-light border border-accent/20">
                        {mappedCount} Obligations Mapped
                      </span>

                      <button
                        onClick={() => {
                          onFilterBySystem(sys.id);
                          onClose();
                        }}
                        className="flex items-center gap-1 text-[11px] font-bold text-accent hover:underline"
                      >
                        <Filter className="w-3 h-3" />
                        <span>Filter Obligations</span>
                      </button>
                    </div>

                  </div>
                );
              })}
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
